import { IF, NO, UIColor, UITextView, UIView, YES } from "uicore-ts"
import { RHCore } from "../../Core/RHCore"
import { CBDialogViewShower } from "../../Custom components/CB/CBDialogViewShower"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHAlertBannerView } from "../../Custom components/RH/RHAlertBannerView"
import { RHTeamUserReferenceResolver } from "../../Custom components/RH/RHTeamRoleSectionView"
import { RHColors, RHFont, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import {
    RHTeamRoleMatrixRole,
    RHTeamRoleMatrixView
} from "../../NewProcurement/ProcurementTeamView"
import { RHProcurementTendererParticipationWithHistory } from "../../SocketClientFunctions"


export class TendererParticipationView extends UIView {

    readonly titleLabel = new UITextView(undefined, UITextView.type.header4)
        .configuredWithObject({
            text: "Pakkuja meeskond",
            backgroundColor: UIColor.transparentColor,
            textColor: RHColors.textPrimary
        })
        .addedAsSubviewToView(this)

    readonly explanationLabel = new UITextView(undefined, UITextView.type.paragraph)
        .configuredWithObject({
            text: "Vastutav ja volitatud isikud saavad pakkumust koostada ning esitada.",
            backgroundColor: UIColor.transparentColor,
            textColor: RHColors.textMuted,
            isSingleLine: NO,
            style: {
                fontSize: RHFont.sizeSmall,
                overflowWrap: "anywhere",
                whiteSpace: "normal"
            }
        })
        .addedAsSubviewToView(this)

    readonly statusBanner = new RHAlertBannerView()

    readonly roleMatrixView = new RHTeamRoleMatrixView()
        .addedAsSubviewToView(this) as RHTeamRoleMatrixView

    participationDidChange?: () => void

    _procurementId = ""
    _participation?: RHProcurementTendererParticipationWithHistory | null
    _isApplyingRoleMatrixChange = NO

    override _focusManager = new RHFocusManager(() => [this.roleMatrixView._focusManager])

    constructor(elementID?: string) {
        super(elementID)
        this.roleMatrixView.addsCurrentUserWhenMissing = NO
        this._configureRoleMatrixPresentation([])
        this.roleMatrixView.teamDidChange = () => {
            void this._applyRoleMatrixChange()
        }

        this.roleMatrixView.currentUserSelfAdditionWasRequested = role => {
            if (
                role !== "authorized" ||
                !this._canCurrentUserAddSelfToParticipation()
            ) {
                return
            }
            this._presentCurrentUserTeamSelfAdditionConfirmation()
        }
        this._reloadActionViews()
    }

    async load(procurementId: string) {
        this._procurementId = procurementId
        this.loading = YES

        try {
            this._participation = await RHCore.sharedInstance.procurementStore
                .retrieveTendererParticipation(procurementId)
            await RHCore.sharedInstance.companyAffiliationStore.retrieveMyCompanyAffiliations()
            const offererCompanyId = RHTeamUserReferenceResolver.userIdFromReference(
                this._participation?.offererCompany
            )
            await this.roleMatrixView.loadCompanyUsers(offererCompanyId)
            this._reloadRoleMatrix()
            this._reloadActionViews()
        }
        catch (error) {
            console.error("Tenderer participation load error:", error)
            this.statusBanner.configuredWithObject({
                alertType: RHAlertBannerView.type.Error,
                text: "Hankel osalemise andmete laadimine ebaõnnestus."
            })
            this.statusBanner.addedAsSubviewToView(this)
        }
        finally {
            this.loading = NO
            this.setNeedsLayoutUpToRootView()
        }
    }

    _configureRoleMatrixPresentation(editableRoles: RHTeamRoleMatrixRole[]) {
        this.roleMatrixView.configureRoleMatrixPresentation({
            visibleRoles: ["responsible", "authorized"],
            editableRoles,
            roleDescriptions: {
                responsible: "Vastutav isik registreeris ettevõtte hankele ning saab pakkumust koostada ja esitada.",
                authorized: "Volitatud isik saab pakkumust koostada ja esitada."
            },
            canCurrentUserChangeOwnRole: NO,
            shouldShowHeaderContent: NO,
            shouldShowCompanyMemberInvitationNotice: NO
        })
    }

    _reloadRoleMatrix() {
        const editableRoles: RHTeamRoleMatrixRole[] = []
        if (this._canCurrentUserManageRoleAssignments()) {
            editableRoles.push("authorized")
        }
        this._configureRoleMatrixPresentation(editableRoles)
        this.roleMatrixView.configureRoleAssignments({
            responsibleUser: this._participation?.registeredBy,
            authorizedUsers: this._participation?.attentionUsers,
            observerUsers: this._participation?.observerUsers
        })
        this.roleMatrixView.canCurrentUserAddSelfToTeam =
            this._canCurrentUserAddSelfToParticipation()
        this.roleMatrixView.configureHistoryEntries(this._participation?.history)
    }

    _canCurrentUserManageRoleAssignments(): boolean {
        if (!this._participation) {
            return NO
        }
        const currentUserId = RHTeamUserReferenceResolver.userIdFromReference(
            RHCore.sharedInstance.userProfile
        )
        const responsibleUserId = RHTeamUserReferenceResolver.userIdFromReference(
            this._participation.registeredBy
        )
        const activeCompanyAffiliation = RHCore.sharedInstance.companyAffiliationStore
            .activeCompanyAffiliation
        const activeCompanyId = RHTeamUserReferenceResolver.userIdFromReference(
            activeCompanyAffiliation?.company
        )
        const participationCompanyId = RHTeamUserReferenceResolver.userIdFromReference(
            this._participation.offererCompany
        )
        const hasDirectActiveAffiliation = activeCompanyId === participationCompanyId &&
            activeCompanyAffiliation?.source !== "relationship" &&
            activeCompanyAffiliation?.membershipStatus === "active"
        if (!hasDirectActiveAffiliation) {
            return NO
        }
        const isCurrentUserAuthorized = (this._participation.attentionUsers ?? []).some(user => {
            return RHTeamUserReferenceResolver.userIdFromReference(user) === currentUserId
        })
        return currentUserId === responsibleUserId ||
            isCurrentUserAuthorized ||
            activeCompanyAffiliation?.isAdministrator === YES
    }

    _canCurrentUserAddSelfToParticipation(): boolean {
        if (!this._canCurrentUserManageRoleAssignments()) {
            return NO
        }
        return RHCore.sharedInstance.companyAffiliationStore
            .activeCompanyAffiliation?.isAdministrator === YES
    }

    _reloadActionViews() {
        this.statusBanner.removeFromSuperview()

        if (this._participation == null) {
            this.statusBanner.configuredWithObject({
                alertType: RHAlertBannerView.type.Info,
                text: "Meeskonna haldamine avaneb pärast hankele registreerumist."
            })
            this.statusBanner.addedAsSubviewToView(this)
        }
    }

    async _applyRoleMatrixChange() {
        if (this._participation == null || this._procurementId.length === 0) {
            return
        }
        if (this._isApplyingRoleMatrixChange === YES) {
            this._reloadRoleMatrix()
            return
        }

        const previousParticipation = this._participation
        try {
            this._isApplyingRoleMatrixChange = YES
            this.roleMatrixView.loading = YES
            this._participation = await RHCore.sharedInstance.procurementStore
                .updateTendererParticipationAttentionUsers(
                    this._procurementId,
                    this.roleMatrixView.authorizedUserIds,
                    this.roleMatrixView.observerUserIds
                )
            this._reloadRoleMatrix()
            this._reloadActionViews()
            this.participationDidChange?.()
        }
        catch (error) {
            console.error("Tenderer participation update error:", error)
            this._participation = previousParticipation
            this._reloadRoleMatrix()
            let errorMessage = "Pakkuja meeskonna uuendamine ebaõnnestus."
            if (error instanceof Error && error.message) {
                errorMessage = error.message
            }
            CBDialogViewShower.alert(errorMessage)
        }
        finally {
            this.roleMatrixView.loading = NO
            this._isApplyingRoleMatrixChange = NO
            this.setNeedsLayoutUpToRootView()
        }
    }

    _presentCurrentUserTeamSelfAdditionConfirmation() {
        const shower = CBDialogViewShower.showQuestionDialog(
            "Kinnitan huvide konflikti puudumise"
        )
        shower.dialogView.view.yesButton.titleLabel.text = "Kinnitan"
        shower.dialogView.view.noButton.titleLabel.text = "Tühista"
        shower.dialogView.view.setNeedsLayoutUpToRootView()

        shower.yesButtonWasPressed = async () => {
            shower.dialogView.dismiss()
            await this._addCurrentUserToParticipation()
        }
    }

    async _addCurrentUserToParticipation() {
        if (
            this._procurementId.length === 0 ||
            !this._canCurrentUserAddSelfToParticipation()
        ) {
            return
        }

        try {
            this.loading = YES
            this._participation = await RHCore.sharedInstance.procurementStore
                .addCurrentUserToTendererParticipation(this._procurementId)
            this._reloadRoleMatrix()
            this._reloadActionViews()
            this.participationDidChange?.()
        }
        catch (error) {
            console.error("Add current user to tenderer participation error:", error)
            let errorMessage = "Pakkuja meeskonda volitatud isikuna lisamine ebaõnnestus."
            if (error instanceof Error && error.message) {
                errorMessage = error.message
            }
            CBDialogViewShower.alert(errorMessage)
        }
        finally {
            this.loading = NO
            this.setNeedsLayoutUpToRootView()
        }
    }

    _flowSteps(): Array<{ view: UIView, gapBefore: number }> {
        const steps: Array<{ view: UIView, gapBefore: number }> = [
            { view: this.titleLabel, gapBefore: 0 },
            { view: this.explanationLabel, gapBefore: RHSpacing.tiny }
        ]
        if (this.statusBanner.superview === this) {
            steps.push({ view: this.statusBanner, gapBefore: RHSpacing.small })
        }
        steps.push({ view: this.roleMatrixView, gapBefore: RHSpacing.medium })
        return steps
    }

    override layoutSubviews() {
        super.layoutSubviews()
        const padding = RHSpacing.medium
        const bounds = this.contentBoundsWithInset(padding)
        const steps = this._flowSteps()

        let previousFrame = bounds
        steps.forEach((step, index) => {
            const rowHeight = step.view.intrinsicContentHeight(bounds.width)
            const frame = IF(index === 0)(() =>
                bounds.rectangleWithHeight(rowHeight)
            ).ELSE(() =>
                previousFrame.rectangleForNextRow(step.gapBefore, rowHeight)
            )
            frame.assignedAsFrameOfView(step.view)
            previousFrame = frame
        })
    }

    override intrinsicContentHeight(constrainingWidth = 0) {
        const padding = RHSpacing.medium
        const contentWidth = Math.max(0, constrainingWidth - padding * 2)
        let height = padding * 2
        this._flowSteps().forEach(step => {
            height += step.gapBefore + step.view.intrinsicContentHeight(contentWidth)
        })
        return height
    }

}
